import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiDatabase2Line, RiCheckLine } from '@remixicon/react';
import {
  CopyButton,
  ExternalLink,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const RDSMySQLCDCInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const templateType = annotations['infrastructure.backstage.io/type'] || '';

  const rdsEndpoint = annotations['mysql.infrastructure/rds-endpoint'];
  const rdsAddress = annotations['mysql.infrastructure/host'];
  const rdsPort = annotations['mysql.infrastructure/port'];
  const databaseName = annotations['mysql.infrastructure/database'];
  const rdiUsername = annotations['mysql.infrastructure/user'];
  const rdiPassword = annotations['mysql.infrastructure/password'];
  const masterUsername = annotations['mysql.infrastructure/master-username'];
  const masterPassword = annotations['mysql.infrastructure/master-password'];
  const mysqlVersion = annotations['mysql.infrastructure/version'];
  const connectionString = annotations['mysql.infrastructure/connection-string'];
  const mysqlCommand = annotations['mysql.infrastructure/mysql-command'];

  const rdsInstanceId = annotations['mysql.infrastructure/rds-instance-id'];
  const multiAZ = annotations['mysql.infrastructure/multi-az'] || 'false';
  const instanceClass = annotations['mysql.infrastructure/instance-class'];
  const allocatedStorage = annotations['mysql.infrastructure/allocated-storage'];
  const storageType = annotations['mysql.infrastructure/storage-type'];
  const availabilityZone = annotations['mysql.infrastructure/availability-zone'];
  const parameterGroup = annotations['mysql.infrastructure/parameter-group'];

  const cdcEnabled = annotations['mysql.infrastructure/cdc-enabled'];
  const binlogFormat = annotations['mysql.infrastructure/binlog-format'];
  const binlogRowImage = annotations['mysql.infrastructure/binlog-row-image'];

  const rdiConfigEncoded = annotations['mysql.infrastructure/rdi-config'];

  const consoleUrl = annotations['mysql.infrastructure/console-url'];
  const awsRegion = annotations['mysql.infrastructure/aws-region'];

  const vpcId = annotations['mysql.infrastructure/vpc-id'];
  const securityGroupId = annotations['mysql.infrastructure/security-group-id'];

  const hasRDSMySQLCDCInfo = templateType === 'aws-rds-mysql-cdc-source' && rdsEndpoint;

  if (!hasRDSMySQLCDCInfo) {
    return null;
  }

  const rdiConfig = useMemo(() => {
    if (!rdiConfigEncoded) return '';
    try {
      return window.atob(rdiConfigEncoded);
    } catch (e) {
      console.error('Failed to decode RDI config:', e);
      return '';
    }
  }, [rdiConfigEncoded]);

  const host = rdsAddress || 'Pending...';
  const port = rdsPort || '3306';
  const database = databaseName || 'rdi_source';
  const user = rdiUsername || 'rdi_user';

  const fallbackRdiConfig = `connections:
  source:
    type: mysql
    host: ${host}
    port: ${port}
    database: ${database}
    user: ${user}
    password: ${rdiPassword || '<your_password>'}

applier:
  target_data_type: hash

sources:
  - name: rds-mysql-source
    connector:
      class: io.debezium.connector.mysql.MySqlConnector
      database.hostname: ${host}
      database.port: ${port}
      database.user: ${user}
      database.password: ${rdiPassword || '<your_password>'}
      database.server.name: rds_mysql
      database.include.list: ${database}
      table.include.list: ${database}.*
      snapshot.mode: initial
      binlog.buffer.size: 8192`;

  const rdiConfigText = rdiConfig || fallbackRdiConfig;

  return (
    <InfoCard title="AWS RDS MySQL CDC Source" icon={<RiDatabase2Line size={20} />}>
      <InfraStack>
        <InfraSection title="🗄️ RDS MySQL Configuration">
          <InfraField
            label="Deployment:"
            value={<InfraTagValue>AWS RDS (Managed)</InfraTagValue>}
            mono={false}
          />
          {rdsInstanceId && (
            <InfraField label="RDS Instance ID:" value={rdsInstanceId} />
          )}
          {mysqlVersion && (
            <InfraField
              label="MySQL Version:"
              value={<InfraTagValue>{`MySQL ${mysqlVersion}`}</InfraTagValue>}
              mono={false}
            />
          )}
          {instanceClass && (
            <InfraField label="Instance Class:" value={instanceClass} />
          )}
          {allocatedStorage && (
            <InfraField
              label="Storage:"
              value={`${allocatedStorage} GB (${storageType || 'gp3'})`}
            />
          )}
          <InfraField
            label="Multi-AZ:"
            value={
              <InfraTagValue>
                {multiAZ === 'true' ? 'Enabled' : 'Disabled (Single-AZ)'}
              </InfraTagValue>
            }
            mono={false}
          />
          {availabilityZone && (
            <InfraField label="Availability Zone:" value={availabilityZone} />
          )}
          {parameterGroup && (
            <InfraField label="Parameter Group:" value={parameterGroup} />
          )}
          {awsRegion && <InfraField label="AWS Region:" value={awsRegion} />}
          {consoleUrl && (
            <InfraField
              label="AWS Console:"
              value={<ExternalLink href={consoleUrl}>View in AWS Console</ExternalLink>}
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🔄 CDC Configuration">
          {cdcEnabled === 'true' && (
            <InfraField
              label="CDC Status:"
              value={
                <InfraTagRow>
                  <InfraTag>
                    <RiCheckLine size={14} style={{ marginRight: 4 }} />
                    Enabled
                  </InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
          {binlogFormat && (
            <InfraField
              label="Binlog Format:"
              value={<InfraTagValue>{binlogFormat}</InfraTagValue>}
              mono={false}
            />
          )}
          {binlogRowImage && (
            <InfraField
              label="Binlog Row Image:"
              value={<InfraTagValue>{binlogRowImage}</InfraTagValue>}
              mono={false}
            />
          )}
          <Text variant="body-medium" style={{ marginTop: '0.75rem', display: 'block' }}>
            ✅ This RDS instance is pre-configured for CDC with:
          </Text>
          <Text variant="body-medium" style={{ marginLeft: '1.25rem', display: 'block' }}>
            • Binlog format set to ROW
          </Text>
          <Text variant="body-medium" style={{ marginLeft: '1.25rem', display: 'block' }}>
            • Binlog row image set to FULL
          </Text>
          <Text variant="body-medium" style={{ marginLeft: '1.25rem', display: 'block' }}>
            • RDI user created with replication permissions
          </Text>
          <Text variant="body-medium" style={{ marginLeft: '1.25rem', display: 'block' }}>
            • Automatic setup via Terraform
          </Text>
        </InfraSection>

        {(vpcId || securityGroupId) && (
          <InfraSection title="🌐 Network Configuration">
            {vpcId && <InfraField label="VPC ID:" value={vpcId} />}
            {securityGroupId && (
              <InfraField label="Security Group:" value={securityGroupId} />
            )}
          </InfraSection>
        )}

        <InfraSection title="🔌 Connection Details">
          <InfraField label="Endpoint:" value={rdsEndpoint || 'Pending...'} />
          <InfraField label="Host:" value={host} />
          <InfraField label="Port:" value={port} />
          <InfraField label="Database:" value={database} />
          <Text variant="body-medium" style={{ marginTop: '1rem', display: 'block' }}>
            Master Credentials:
          </Text>
          <InfraField label="Username:" value={masterUsername || 'admin'} />
          <InfraField label="Password:" value={masterPassword || 'See Terraform outputs'} />
          <Text variant="body-medium" style={{ marginTop: '1rem', display: 'block' }}>
            RDI User Credentials:
          </Text>
          <InfraField label="Username:" value={user} />
          <InfraField label="Password:" value={rdiPassword || 'See Terraform outputs'} />
          {connectionString && (
            <>
              <InfraField label="Connection String:" value="" mono={false} />
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{connectionString}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={connectionString} />
                </div>
              </div>
            </>
          )}
          {mysqlCommand && (
            <>
              <InfraField label="MySQL CLI Command:" value="" mono={false} />
              <div style={{ position: 'relative' }}>
                <InfraCodeBlock variant="light">{mysqlCommand}</InfraCodeBlock>
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={mysqlCommand} />
                </div>
              </div>
            </>
          )}
        </InfraSection>

        <InfraSection title="⚙️ Redis Data Integration (RDI) Configuration">
          <Text variant="body-medium">RDI Config Snippet:</Text>
          <div style={{ position: 'relative' }}>
            <InfraCodeBlock variant="light">{rdiConfigText}</InfraCodeBlock>
            <div style={{ position: 'absolute', top: 4, right: 4 }}>
              <CopyButton text={rdiConfigText} />
            </div>
          </div>
          <Text variant="body-medium" style={{ marginTop: '0.75rem', display: 'block' }}>
            💡 Tip: Copy this configuration and use it when creating an RDI Server via the Backstage template.
          </Text>
        </InfraSection>
      </InfraStack>
    </InfoCard>
  );
};
